import axios from '@/plugins/axios'
import { auth } from '@/stores/auth'
import { onBeforeUnmount, watch } from 'vue'
import type { record as RecordFn } from 'rrweb'
import type { CursorPage, ReplaySession } from './tracking'

const endpoint = '/api-proxy/api/v1/observability'
const sessionStorageKey = 'observability-replay-session-id'
const sessionUserStorageKey = 'observability-replay-session-user-id'
const replayMode = import.meta.env.VITE_SESSION_REPLAY_MODE === 'always' ? 'always' : 'errors'

export const trackingEnabled = import.meta.env.VITE_OBSERVABILITY_ENABLED !== 'false'

let stopRecording: (() => void) | undefined
let recorder: typeof RecordFn | undefined
let flushTimer: ReturnType<typeof setInterval> | undefined
let flushInProgress = false
const pendingEvents: unknown[] = []
const maxBufferedEvents = 2_000

export type ClientErrorSource = 'evaluation' | 'video-call' | 'group-call'

export async function startSession(): Promise<ReplaySession | null> {
  if (!trackingEnabled || !auth().user) return null

  const { data } = await axios.post<ReplaySession>(`${endpoint}/replay-sessions`)
  return data
}

export async function getOrCreateSession(): Promise<number | null> {
  if (!trackingEnabled || !auth().user) return null

  const currentUserID = String((auth().user as { id?: string | number }).id ?? '')
  const stored = sessionStorage.getItem(sessionStorageKey)
  if (
    stored &&
    sessionStorage.getItem(sessionUserStorageKey) === currentUserID &&
    Number.isSafeInteger(Number(stored)) &&
    Number(stored) > 0
  ) {
    return Number(stored)
  }

  sessionStorage.removeItem(sessionStorageKey)
  sessionStorage.removeItem(sessionUserStorageKey)

  const session = await startSession()
  if (!session) return null

  sessionStorage.setItem(sessionStorageKey, String(session.id))
  sessionStorage.setItem(sessionUserStorageKey, currentUserID)
  return session.id
}

export function useTrackingSession() {
  if (!trackingEnabled || import.meta.env.SSR) return

  const authStore = auth()
  const stopWatching = watch(
    () => authStore.user,
    (user) => {
      if (user) {
        void beginRecording()
      } else {
        stop()
        sessionStorage.removeItem(sessionStorageKey)
        sessionStorage.removeItem(sessionUserStorageKey)
      }
    },
    { immediate: true }
  )

  const onPageHide = () => {
    void sendSessionReplay()
  }
  const onWindowError = (event: ErrorEvent) => {
    recordError(event.error ?? event.message)
    void sendSessionReplay()
  }
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    recordError(event.reason)
    void sendSessionReplay()
  }
  const onRequestError = (event: Event) => {
    recordError((event as CustomEvent<unknown>).detail)
    void sendSessionReplay()
  }
  window.addEventListener('pagehide', onPageHide)
  window.addEventListener('error', onWindowError)
  window.addEventListener('unhandledrejection', onUnhandledRejection)
  window.addEventListener('observability:request-error', onRequestError)

  onBeforeUnmount(() => {
    stopWatching()
    window.removeEventListener('pagehide', onPageHide)
    window.removeEventListener('error', onWindowError)
    window.removeEventListener('unhandledrejection', onUnhandledRejection)
    window.removeEventListener('observability:request-error', onRequestError)
    stop()
  })
}

async function beginRecording() {
  if (stopRecording || typeof window === 'undefined') return

  const rrweb = await import('rrweb')
  recorder = rrweb.record
  stopRecording = recorder({
    emit(event) {
      pendingEvents.push(event)
      if (pendingEvents.length > maxBufferedEvents) {
        const latestCheckout = pendingEvents.findIndex(
          (candidate, index) => index > 0 && isFullSnapshot(candidate)
        )
        if (latestCheckout > 0) pendingEvents.splice(0, latestCheckout)
      }
    },
    // A replay is an incident-support tool. Inputs are always masked; pages
    // can additionally mark sensitive areas with rr-block / rr-mask.
    maskAllInputs: true,
    blockClass: 'rr-block',
    maskTextClass: 'rr-mask',
    checkoutEveryNth: 200
  })

  if (replayMode === 'always') {
    flushTimer = window.setInterval(() => void flushEvents(), 10_000)
  }
}

function stop() {
  stopRecording?.()
  stopRecording = undefined
  recorder = undefined
  if (flushTimer) window.clearInterval(flushTimer)
  flushTimer = undefined
  pendingEvents.length = 0
}

function isFullSnapshot(event: unknown) {
  return (
    typeof event === 'object' &&
    event !== null &&
    'type' in event &&
    (event as { type?: unknown }).type === 2
  )
}

async function flushEvents() {
  if (flushInProgress || pendingEvents.length === 0) return

  const sessionId = await getOrCreateSession()
  if (!sessionId) return

  flushInProgress = true
  const batch = pendingEvents.splice(0, pendingEvents.length)
  try {
    await axios.post(`${endpoint}/replay-sessions/${sessionId}/events`, { events: batch })
  } catch (error) {
    pendingEvents.unshift(...batch)
    throw error
  } finally {
    flushInProgress = false
  }
}

export async function sendSessionReplay() {
  if (!trackingEnabled) return
  try {
    await flushEvents()
  } catch {
    // A telemetry upload must never interfere with the user-facing action.
  }
}

export function recordError(error: unknown) {
  if (!trackingEnabled) return

  const candidate = error instanceof Error ? error : new Error(String(error))
  recorder?.addCustomEvent('error', {
    name: candidate.name,
    message: candidate.message.slice(0, 2_000),
    stack: candidate.stack?.slice(0, 8_000),
    timestamp: new Date().toISOString()
  })
}

/** Records a user-safe client error sample and adds it to the current replay. */
export function reportClientError(source: ClientErrorSource, operation: string, _error: unknown) {
  if (!trackingEnabled || import.meta.env.SSR) return

  // The operation identifies the failure in the dashboard. Do not transmit
  // a browser/server error message here: it may contain user-provided data.
  const message = 'client operation failed'
  recordError(new Error(`${source}/${operation}: ${message}`))

  void axios
    .post(`${endpoint}/errors/client`, {
      source,
      operation,
      message,
      path: window.location.pathname
    })
    .catch(() => undefined)
  void sendSessionReplay()
}

export async function fetchReplaySessions(params: { cursor?: string; limit?: number } = {}) {
  const { data } = await axios.get<CursorPage<ReplaySession>>(`${endpoint}/replay-sessions`, {
    params
  })
  return data
}

export async function fetchReplayEvents(id: number) {
  const { data } = await axios.get<{ id: number; events: unknown[] }>(
    `${endpoint}/replay-sessions/${id}/events`
  )
  return data
}
